import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import LessonCard from "../components/LessonCard";
import VideoCard from "../components/VideoCard";
import BackButton from "../components/BackButton";
import SuggestTopicDialog from "../components/SuggestTopicDialog";
import { isSuperAdmin } from "../utils/users";
import { ChevronDownIcon, LightBulbIcon } from "@heroicons/react/24/outline";

const AGE_GROUP_OPTIONS = ["7-9", "9-11", "11-12"];

function TopicLessons({ userType = "user" }) {
  const { topic } = useParams();
  const [lessons, setLessons] = useState([]);
  const [videos, setVideos] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filters, setFilters] = useState({
    lessons: true,
    videos: true,
  });
  const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
  const [showAgeGroupDropdown, setShowAgeGroupDropdown] = useState(false);
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const dropdownRef = useRef(null);
  const ageGroupDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
      if (
        ageGroupDropdownRef.current &&
        !ageGroupDropdownRef.current.contains(event.target)
      ) {
        setShowAgeGroupDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch lessons and videos
  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch lessons from Firestore
        const lessonsRef = collection(db, "node_lessons");
        const q = query(lessonsRef, orderBy("title"));
        const querySnapshot = await getDocs(q);

        const lessonsData = [];
        querySnapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data(),
            type: "lesson",
          });
        });

        // Load videos from Firebase
        const videosQuery = query(
          collection(db, "videos"),
          orderBy("created_date", "desc"),
        );
        const videosSnapshot = await getDocs(videosQuery);
        const videosData = [];
        videosSnapshot.forEach((doc) => {
          videosData.push({
            id: doc.id,
            ...doc.data(),
            type: "video",
          });
        });

        // Filter lessons based on user type
        // Super admins can see all lessons (including preview), others only see published lessons
        const filteredLessonsData = isSuperAdmin(userType)
          ? lessonsData
          : lessonsData.filter((lesson) => lesson.isPreview !== true);

        setLessons(filteredLessonsData);
        setVideos(videosData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching content:", error);
        setError("Failed to load content");
        setLoading(false);
      }
    };

    fetchContent();
  }, [topic, userType]);

  // Filter content based on topic, search term, content type, and age groups (lessons only)
  useEffect(() => {
    let allContent = [];

    // Add lessons if filter is enabled (filter by age_groups when selected)
    if (filters.lessons) {
      let filteredLessons = lessons;
      if (selectedAgeGroups.length > 0) {
        filteredLessons = lessons.filter((lesson) => {
          const lessonAgeGroups = (lesson.age_groups || []).map((item) =>
            typeof item === "string" ? item : (item && item.description) || "",
          );
          return selectedAgeGroups.some((ag) => lessonAgeGroups.includes(ag));
        });
      }
      allContent = [...allContent, ...filteredLessons];
    }

    // Add videos if filter is enabled (videos are not filtered by age_groups)
    if (filters.videos) {
      allContent = [...allContent, ...videos];
    }

    if (searchTerm.trim() === "") {
      const topicContent = allContent.filter(
        (item) => item.units && item.units.some((unit) => unit === topic),
      );
      // Sort by title
      topicContent.sort((a, b) => a.title.localeCompare(b.title));
      setFilteredContent(topicContent);
    } else {
      const filtered = allContent.filter(
        (item) =>
          item.units &&
          item.units.some((unit) => unit === topic) &&
          item.title.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      // Sort by title
      filtered.sort((a, b) => a.title.localeCompare(b.title));
      setFilteredContent(filtered);
    }
  }, [searchTerm, lessons, videos, topic, filters, selectedAgeGroups]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleFilterChange = (filterType) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const getFilterText = () => {
    if (filters.lessons && filters.videos) return "All Content";
    if (filters.lessons) return "Lessons Only";
    if (filters.videos) return "Videos Only";
    return "No Content";
  };

  const handleAgeGroupToggle = (option) => {
    setSelectedAgeGroups((prev) =>
      prev.includes(option)
        ? prev.filter((ag) => ag !== option)
        : [...prev, option],
    );
  };

  const getAgeGroupFilterText = () => {
    if (selectedAgeGroups.length === 0) return "Age Groups";
    if (selectedAgeGroups.length === AGE_GROUP_OPTIONS.length)
      return "All ages";
    return `${selectedAgeGroups.length} age group${selectedAgeGroups.length === 1 ? "" : "s"}`;
  };

  const formatTopicName = (topicName) => {
    return topicName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Lessons Section */}
      <div className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <BackButton to="/topics" />

          {/* Header with Title and Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
            <h1 className="text-3xl font-bold">{topic}</h1>

            {/* Filter and Search Controls */}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {/* Filter Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2"
                  aria-expanded={showFilterDropdown}
                  aria-haspopup="listbox"
                  aria-label="Filter content type"
                >
                  <span className="text-sm font-medium">{getFilterText()}</span>
                  <ChevronDownIcon className="w-4 h-4 ml-2" />
                </button>

                {showFilterDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.lessons}
                          onChange={() => handleFilterChange("lessons")}
                          className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm">Lessons</span>
                      </label>
                      <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.videos}
                          onChange={() => handleFilterChange("videos")}
                          className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm">Videos</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Ages Dropdown (filters lessons only) */}
              <div className="relative" ref={ageGroupDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowAgeGroupDropdown(!showAgeGroupDropdown)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2"
                  aria-expanded={showAgeGroupDropdown}
                  aria-haspopup="listbox"
                  aria-label="Filter by age group"
                >
                  <span className="text-sm font-medium">
                    {getAgeGroupFilterText()}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 ml-2" />
                </button>

                {showAgeGroupDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      {AGE_GROUP_OPTIONS.map((option) => (
                        <label
                          key={option}
                          className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAgeGroups.includes(option)}
                            onChange={() => handleAgeGroupToggle(option)}
                            className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Bar */}
              <div className="relative">
                <label htmlFor="topic-lessons-search" className="sr-only">
                  Search lessons and videos by title or keyword
                </label>
                <input
                  id="topic-lessons-search"
                  type="text"
                  placeholder="Search by title or keyword..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full sm:w-96 px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                    aria-label="Clear search"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading content...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {!loading && !error && filteredContent.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedAgeGroups.length > 0
                  ? `No content found matching your filters in ${formatTopicName(
                      topic,
                    )}.`
                  : `No content found for ${formatTopicName(topic)}.`}
              </p>
              <button
                onClick={() => setShowSuggestDialog(true)}
                className="inline-flex items-center gap-2 px-8 py-4 text-lg rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors font-medium"
              >
                <LightBulbIcon className="w-6 h-6" />
                Suggest a Lesson Topic
              </button>
            </div>
          )}

          {!loading && !error && filteredContent.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min">
                {filteredContent.map((item) =>
                  item.type === "lesson" ? (
                    <LessonCard
                      key={item.id}
                      lesson={item}
                      returnPath={`/topics/${topic}`}
                    />
                  ) : (
                    <VideoCard key={item.id} video={item} />
                  ),
                )}
              </div>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowSuggestDialog(true)}
                  className="inline-flex items-center gap-2 px-8 py-4 text-lg rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors font-medium"
                >
                  <LightBulbIcon className="w-6 h-6" />
                  Suggest a Lesson Topic
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <SuggestTopicDialog
        isOpen={showSuggestDialog}
        onClose={() => setShowSuggestDialog(false)}
      />
    </div>
  );
}

export default TopicLessons;
